import { useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import classNames from "classnames";
import { JoyRide } from "components/molecules/JoyRide";
import {
  useScreenSize,
  useScrollRestoration,
  useIframeModalScrollRestoration,
} from "hooks";
import { segmentReFetchContext } from "context";
import { AIMCATChat } from "../../organisms/AiMcatChat";
import { Sidebar } from "components/organisms/Sidebar";
import { AppFooter } from "components/organisms/AppFooter";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
}

interface SelectedBtnState {
  [key: string]: boolean;
  "1": boolean;
  "2": boolean;
  "3": boolean;
  "4": boolean;
}

export const MainPage = ({ theme }: IProps) => {
  const screen = useScreenSize();

  const [selectedBtn, setSelectedBtn] = useState<SelectedBtnState>({
    "1": true,
    "2": false,
    "3": false,
    "4": false,
  });

  const [selectedSession, setSelectedSession] = useState<
    null | string | { [key: number]: string }
  >({ 0: "Recent and Upcoming Sessions" });

  const [data, setData] = useState<any[]>([]);

  const [page, setPage] = useState<number>(1);

  const mainWrapperRef = useRef<HTMLDivElement>(null);
  /** Grows with routed content; ResizeObserver target for scroll restoration. */
  const mainScrollInnerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();

  // Replaces an unconditional `scrollTo(0, 0)` on every pathname change. The
  // hook scrolls to top on PUSH/REPLACE (forward navigations) and restores the
  // previously saved offset on POP (browser back / `navigate(-1)`), so the
  // user lands where they left off when returning from e.g. /drill or
  // /reschedule-event back to /study-planner.
  useScrollRestoration(mainWrapperRef, mainScrollInnerRef);

  // The iframe modal is a portal sibling of MainPage and never unmounts the
  // underlying page, so route-based restoration above doesn't fire. This hook
  // captures `mainWrapperRef.scrollTop` on modal open and restores it after
  // close + post-close auto-refresh. Without this, RTK Query cache
  // invalidations triggered by IframeModalManager can layout-shift the
  // planner and silently clamp scroll position on mobile.
  useIframeModalScrollRestoration(mainWrapperRef, mainScrollInnerRef);

  return (
    <div className={classNames("main-page-wrapper")}>
      <JoyRide />
      <Sidebar theme={theme} />

      <div
        ref={mainWrapperRef}
        className={classNames({
          "content-section-wrapper": true,
          "custom-bg-color":
            location.pathname === "/" ||
            location.pathname === "/study-recommendations-tool",
        })}
      >
        <div
          ref={mainScrollInnerRef}
          className={classNames({
            "main-page-detail-section": true,
            "custom-bg-color":
              location.pathname === "/" ||
              location.pathname === "/study-recommendations-tool",
          })}
        >
          <segmentReFetchContext.Provider
            value={{
              data,
              setData,
              selectedBtn,
              setSelectedBtn,
              selectedSession,
              setSelectedSession,
              page,
              setPage,
            }}
          >
            <div className="page-content-fill">
              <Outlet />
            </div>
          </segmentReFetchContext.Provider>
          <AppFooter />
        </div>
      </div>
      {/*  here defines the AI MCAT chat drawer and error alert*/}
      <AIMCATChat theme={theme} />
    </div>
  );
};
